// Shared primitives for the admin area (Step 5e Phase 7). Light theme — tokens from the ops
// board's theme.ts (C/F/M), matching the signed-off mockup palette 1:1.

import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { C, F, M } from '../ops-board/theme';

export const card: CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 };
export const sub: CSSProperties = { color: C.textDim, fontSize: 13, margin: '0 0 14px' };

export function Btn({ kind = 'ghost', onClick, disabled, children, type = 'button' }: {
  kind?: 'primary' | 'ghost' | 'danger' | 'accent'; onClick?: () => void; disabled?: boolean; children: ReactNode; type?: 'button' | 'submit';
}) {
  const base: CSSProperties = { fontFamily: F, fontWeight: 600, fontSize: 13, padding: '8px 15px', borderRadius: 8, border: '1px solid transparent', cursor: disabled ? 'not-allowed' : 'pointer' };
  const styles: Record<string, CSSProperties> = {
    primary: { background: disabled ? C.pill : C.accent, color: disabled ? C.textMuted : '#fff' },
    danger: { background: disabled ? C.pill : C.red, color: disabled ? C.textMuted : '#fff' },
    accent: { background: disabled ? C.pill : C.accent, color: '#fff' },
    ghost: { background: '#fff', borderColor: C.borderMed, color: C.textDim },
  };
  return <button type={type} onClick={onClick} disabled={disabled} style={{ ...base, ...styles[kind] }}>{children}</button>;
}

export function StatusPill({ active }: { active: boolean }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: active ? C.greenLight : C.pill, color: active ? C.green : C.textMuted }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export function Modal({ title, onClose, children, width = 640, dirty = false }: { title: string; onClose: () => void; children: ReactNode; width?: number; dirty?: boolean }) {
  // Guarded close (v0.3.1): a stray backdrop click or Esc on a form with unsaved edits prompts
  // before discarding — Ben hit this editing an asset (a mis-click outside the card snapped back
  // to the list and lost everything). The × / Cancel buttons always close directly (explicit
  // intent), so `onClose` itself is unchanged; only the accidental vectors are guarded.
  const requestClose = () => {
    if (dirty && !window.confirm('Discard your unsaved changes?')) return;
    onClose();
  };
  // PR-000025: a bare backdrop onClick also fires when the mouse went DOWN inside the card
  // (e.g. drag-selecting text before a paste) and came UP on the backdrop — closing the modal
  // (and, from the booking form, revealing the form beneath as if it "reopened"). Guard the
  // backdrop so it closes only when the press BOTH starts and ends on the backdrop itself.
  const downOnBackdrop = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
  // PR-000007 — render into document.body via a portal so the modal escapes any ancestor stacking
  // context (e.g. when opened from the booking form's overlay) AND sits above it (zIndex 1000 > the
  // ops-board ModalOverlay's 300). Behaviour (backdrop click-out, Esc, ×) is unchanged.
  return createPortal(
    <div
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { const started = downOnBackdrop.current; downOnBackdrop.current = false; if (started && e.target === e.currentTarget) requestClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 1000 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width, maxWidth: '100%', fontFamily: F, color: C.text }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, color: C.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div style={{ color: C.red, fontSize: 12.5, background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 6, padding: '6px 10px', marginTop: 8 }}>{children}</div>;
}
export function WarnText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div style={{ color: C.amber, fontSize: 12.5, background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 6, padding: '6px 10px', marginTop: 8 }}>{children}</div>;
}

// ── Generic field-driven form ──────────────────────────────────────────────────
export type FieldType = 'text' | 'number' | 'bool' | 'select' | 'date' | 'textarea';
export interface FieldDef {
  key: string; label: string; type: FieldType;
  required?: boolean; options?: { value: string; label: string }[];
  group?: string; placeholder?: string; help?: string;
  // Opt-in: an EMPTY box on this field is a deliberate CLEAR and is sent as such, rather than
  // being dropped as "not supplied". Without it a value can be set but never unset — the same
  // defect class fixed for preferred_name / pilot_code / mobile on 2026-08-27, which was
  // invisible to any service-level test because the service always honoured an explicit null.
  // Opt-in rather than default because for every other field here "blank" genuinely does mean
  // "leave it alone" (the edit form says so in its header).
  clearable?: boolean;
}
export type FormValues = Record<string, string | number | boolean | undefined>;

const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text, width: '100%' };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, marginBottom: 3, display: 'block' };

export function FieldInput({ field, value, onChange }: { field: FieldDef; value: string | number | boolean | undefined; onChange: (v: string | number | boolean | undefined) => void }) {
  const lbl = <label style={labelStyle}>{field.label}{field.required && <span style={{ color: C.red }}> *</span>}</label>;
  let input: ReactNode;
  if (field.type === 'bool') {
    input = <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent }} />;
    return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>{input}<span style={{ fontSize: 12.5, color: C.textDim }}>{field.label}</span></div>;
  } else if (field.type === 'select') {
    input = (
      <select value={value === undefined ? '' : String(value)} onChange={(e) => onChange(e.target.value || undefined)} style={inputStyle}>
        <option value="">— select —</option>
        {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else if (field.type === 'textarea') {
    input = <textarea value={value === undefined ? '' : String(value)} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value || undefined)} style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }} />;
  } else {
    const t = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
    input = (
      <input type={t} value={value === undefined ? '' : String(value)} placeholder={field.placeholder}
        onChange={(e) => onChange(field.type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : (e.target.value || undefined))}
        style={inputStyle} />
    );
  }
  return <div>{lbl}{input}{field.help && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{field.help}</div>}</div>;
}

// Render grouped fields into a responsive grid and manage local values.
export function FieldGrid({ fields, values, setValues }: { fields: FieldDef[]; values: FormValues; setValues: (v: FormValues) => void }) {
  const groups = new Map<string, FieldDef[]>();
  for (const f of fields) { const g = f.group ?? ''; const arr = groups.get(g) ?? []; arr.push(f); groups.set(g, arr); }
  return (
    <div>
      {[...groups.entries()].map(([g, fs]) => (
        <div key={g} style={{ marginBottom: 14 }}>
          {g && <h3 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: C.textMuted, margin: '6px 0' }}>{g}</h3>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {fs.map((f) => <FieldInput key={f.key} field={f} value={values[f.key]} onChange={(v) => setValues({ ...values, [f.key]: v })} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// Build an API payload from form values: drop undefined/'' optionals; keep booleans/numbers.
export function buildPayload(fields: FieldDef[], values: FormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.key];
    // Required keeps its EXACT prior behaviour (the key is set to undefined, which
    // JSON.stringify then drops, so the server raises its own "required" error). Only the
    // clearable branch is new.
    if (v === undefined || v === '') {
      if (f.required) out[f.key] = v;
      else if (f.clearable) out[f.key] = '';
      continue;
    }
    out[f.key] = v;
  }
  return out;
}

export { C, F, M };
